import CssBaseline from "@mui/material/CssBaseline";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import type React from "react";
import { useAppSelector } from "../../index";
import "@fontsource/roboto";
import "@fontsource/fira-sans-condensed";
import "@fontsource/open-sans";
import "@fontsource/raleway";
//import "@fontsource/roboto/100.css";
//import "@fontsource/roboto/300.css";
//import "@fontsource/roboto/400.css";
//import "@fontsource/roboto/500.css";
//import "@fontsource/roboto/700.css";
//import "@fontsource/roboto/900.css";
//import "@fontsource/fira-sans-condensed/100.css";
//import "@fontsource/fira-sans-condensed/300.css";
//import "@fontsource/fira-sans-condensed/400.css";
//import "@fontsource/fira-sans-condensed/500.css";
//import "@fontsource/fira-sans-condensed/700.css";
//import "@fontsource/fira-sans-condensed/900.css";
//import "@fontsource/open-sans/100.css";
//import "@fontsource/open-sans/300.css";
//import "@fontsource/open-sans/400.css";
//import "@fontsource/open-sans/500.css";
//import "@fontsource/open-sans/700.css";
//import "@fontsource/open-sans/900.css";
//import "@fontsource/raleway/100.css";
//import "@fontsource/raleway/300.css";
//import "@fontsource/raleway/400.css";
//import "@fontsource/raleway/500.css";
//import "@fontsource/raleway/700.css";
//import "@fontsource/raleway/900.css";

import { Category, FileCategory } from "fileParsing/types";
import type { ApplicationState } from "store/index";

// Extends MuiTheme with custom properties in the Typescript way!
declare module "@mui/material/styles" {
  interface Theme {
    // Extend the Theme by anything you want!
    neutral: {
      light: string;
      main: string;
      dark: string;
    };
    landingPage: {
      backgroundImage: string;
    };
    sidebar: {
      config: {
        width: number;
        collapsed: number;
      };
      navigation: {
        width: number;
        collapsed: number;
      };
      headerHeight: number;
      footerHeight: number;
      bottomBarHeight: number;
    };
  }
  interface ThemeOptions {
    // When extending, also extend the createTheme
    neutral: {
      light: string;
      main: string;
      dark: string;
    };
    landingPage: {
      backgroundImage: string;
    };
    sidebar: {
      config: {
        width: number;
        collapsed: number;
      };
      navigation: {
        width: number;
        collapsed: number;
      };
      headerHeight: number;
      footerHeight: number;
      bottomBarHeight: number;
    };
  }
  interface TypographyVariants {
    code: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    code?: React.CSSProperties;
  }
}

declare module "@mui/material/styles/createPalette" {
  interface Palette {
    tertiary?: {
      main?: string;
    };
    categories: {
      [Category.Account]: Palette["primary"];
      [Category.Activity]: Palette["primary"];
      [Category.Contacts]: Palette["primary"];
      [Category.Likes]: Palette["primary"];
      [Category.Location]: Palette["primary"];
      [Category.Media]: Palette["primary"];
      [Category.Messages]: Palette["primary"];
      [Category.Other]: Palette["primary"];
      [Category.PostsAndComments]: Palette["primary"];
      [Category.Security]: Palette["primary"];
    };
    files: {
      [FileCategory.Audio]: Palette["primary"];
      [FileCategory.Document]: Palette["primary"];
      [FileCategory.Other]: Palette["primary"];
      [FileCategory.Picture]: Palette["primary"];
      [FileCategory.Text]: Palette["primary"];
      [FileCategory.Video]: Palette["primary"];
    };
    // providers: {
    //   [Providers.Facebook]: Palette['primary'],
    //   [Providers.Google]: Palette['primary'],
    //   [Providers.Twitter]: Palette['primary'],
    //   [Providers.Reddit]: Palette['primary'],
    //   [Providers.Google]: Palette['primary'],
    //   [Providers.Google]: Palette['primary'],
    // }
  }

  interface TypeBackground {
    tooltip: string;
  }

  interface PaletteOptions {
    tertiary: {
      main: string;
    };
    categories: {
      [Category.Account]: PaletteOptions["primary"];
      [Category.Activity]: PaletteOptions["primary"];
      [Category.Contacts]: PaletteOptions["primary"];
      [Category.Likes]: PaletteOptions["primary"];
      [Category.Location]: PaletteOptions["primary"];
      [Category.Media]: PaletteOptions["primary"];
      [Category.Messages]: PaletteOptions["primary"];
      [Category.Other]: PaletteOptions["primary"];
      [Category.PostsAndComments]: PaletteOptions["primary"];
      [Category.Security]: PaletteOptions["primary"];
    };
    files: {
      [FileCategory.Audio]: PaletteOptions["primary"];
      [FileCategory.Document]: PaletteOptions["primary"];
      [FileCategory.Other]: PaletteOptions["primary"];
      [FileCategory.Picture]: PaletteOptions["primary"];
      [FileCategory.Text]: PaletteOptions["primary"];
      [FileCategory.Video]: PaletteOptions["primary"];
    };
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    code: true;
  }
}
/*********************************************************************
 *                          The dark theme
 ********************************************************************/

const darkTheme = createTheme({
  // Define primary and secondary color
  palette: {
    mode: "dark",
    primary: { main: "#5FAF77" },
    secondary: { main: "#43849e" },
    tertiary: { main: "#af5f97" },
    info: { main: "#55acff" },
    success: { main: "#6dfc6d" },
    error: { main: "#fc6d6d" },
    background: {
      default: "#1a1818",
      paper: "#282a2a",
      tooltip: "#49454F",
    },
    categories: {
      [Category.Account]: { main: "#5dacd9" },
      [Category.Activity]: { main: "#6ed355" },
      [Category.Contacts]: { main: "#9e8ada" },
      [Category.Likes]: { main: "#67cea7" },
      [Category.Location]: { main: "#ce9549" },
      [Category.Media]: { main: "#e55f45" },
      [Category.Messages]: { main: "#d88299" },
      [Category.Other]: { main: "#aab67a" },
      [Category.PostsAndComments]: { main: "#d758d4" },
      [Category.Security]: { main: "#d1d648" },
    },
    files: {
      [FileCategory.Audio]: { main: "#f4cae4" },
      [FileCategory.Document]: { main: "#b3e2cd" },
      [FileCategory.Other]: { main: "#fdcdac" },
      [FileCategory.Picture]: { main: "#fff2ae" },
      [FileCategory.Text]: { main: "#e6f5c9" },
      [FileCategory.Video]: { main: "#fbf5e8" },
    },
  },
  neutral: {
    light: "#666",
    main: "#444",
    dark: "#222",
  },
  typography: {
    fontFamily: ['"Open Sans"', "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
    h1: {
      fontFamily: "Fira Sans Condensed",
      fontWeight: 800,
      fontSize: "4rem",
    },
    h2: {
      fontFamily: "Fira Sans Condensed",
      fontSize: "2.2rem",
    },
    h3: {
      fontFamily: "Fira Sans Condensed",
      fontSize: "1.9rem",
    },
    h4: {
      fontFamily: "Fira Sans Condensed",
      fontSize: "1.6rem",
    },
    h5: {
      fontFamily: "Fira Sans Condensed",
      fontSize: "1.3rem",
    },
    h6: {
      fontFamily: "Fira Sans Condensed",
      fontSize: "1.1rem",
    },
    button: {
      fontFamily: "Roboto",
      //fontWeight: 400,
      fontSize: "0.9rem",
    },
    code: {
      fontFamily: "monospace",
      fontSize: "0.75rem",
      whiteSpace: "break-spaces",
    },
  },
  landingPage: {
    backgroundImage: "",
  },
  sidebar: {
    config: {
      width: 240,
      collapsed: 0,
    },
    navigation: {
      width: 220,
      collapsed: 64,
    },
    headerHeight: 56, // Titlebar (height search and FullScreen-Button)
    footerHeight: 10,
    bottomBarHeight: 60,
  },
  // global style override of mui components
  components: {
    MuiLink: {
      styleOverrides: {
        root: {
          color: "#7daee1",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorInherit: {
          backgroundColor: "#303030",
          color: "#fff",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          "*::-webkit-scrollbar": {
            width: "0.5em",
            height: "0.5em",
          },
          "*::-webkit-scrollbar-track": {
            WebkitBoxShadow: "inset 0 0 6px rgba(5,5,5,0.00)",
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(120,120,120,0.4)",
            borderRadius: "10px",
          },
        },
      },
    },
  },
});

/*********************************************************************
 *                          The light theme
 ********************************************************************/

const lightTheme = createTheme({
  // Define primary and secondary color
  palette: {
    mode: "light",
    primary: { main: "#5FAF77" },
    secondary: { main: "#43849e" },
    tertiary: { main: "#af5f97" },
    info: { main: "#55acff" },
    success: { main: "#6dfc6d" },
    error: { main: "#fc6d6d" },
    background: {
      default: "#fff",
      paper: "#f8f8ff",
      tooltip: "#d9d2e3",
    },
    categories: {
      [Category.Account]: { main: "#5dacd9" },
      [Category.Activity]: { main: "#6ed355" },
      [Category.Contacts]: { main: "#9e8ada" },
      [Category.Likes]: { main: "#67cea7" },
      [Category.Location]: { main: "#ce9549" },
      [Category.Media]: { main: "#e55f45" },
      [Category.Messages]: { main: "#d88299" },
      [Category.Other]: { main: "#aab67a" },
      [Category.PostsAndComments]: { main: "#d758d4" },
      [Category.Security]: { main: "#d1d648" },
    },
    files: {
      [FileCategory.Audio]: { main: "#f4cae4" },
      [FileCategory.Document]: { main: "#b3e2cd" },
      [FileCategory.Other]: { main: "#fdcdac" },
      [FileCategory.Picture]: { main: "#fff2ae" },
      [FileCategory.Text]: { main: "#e6f5c9" },
      [FileCategory.Video]: { main: "#fbf5e8" },
    },
    /*
    background: {
      //default: '#fafafa',
      //paper: '#fff',
    },
    text: {
      //primary: 'rgba(0,0,0,1)',
      //secondary: 'rgba(0,0,0,0.6)',
      //disabled: 'rgba(0,0,0,0.4)',
    },
    */
  },
  neutral: {
    light: "#eee",
    main: "#aaa",
    dark: "#555",
  },
  typography: {
    fontFamily: ['"Open Sans"', "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
    h1: {
      fontFamily: "Fira Sans Condensed",
      fontSize: "4rem",
    },
    h2: {
      fontFamily: "Fira Sans Condensed",
      fontSize: "2.2rem",
    },
    h3: {
      fontFamily: "Fira Sans Condensed",
      fontSize: "1.9rem",
    },
    h4: {
      fontFamily: "Fira Sans Condensed",
      fontSize: "1.6rem",
    },
    h5: {
      fontFamily: "Fira Sans Condensed",
      fontSize: "1.3rem",
    },
    h6: {
      fontFamily: "Fira Sans Condensed",
      fontSize: "1.1rem",
    },
    button: {
      fontFamily: "Roboto",
      fontWeight: 400,
      fontSize: "0.9rem",
    },
    code: {
      fontFamily: "monospace",
      fontSize: "0.75rem",
      whiteSpace: "break-spaces",
    },
  },
  landingPage: {
    backgroundImage: "",
  },
  sidebar: {
    config: {
      width: 240,
      collapsed: 0,
    },
    navigation: {
      width: 220,
      collapsed: 64,
    },
    headerHeight: 56, // Titlebar (height search and FullScreen-Button),
    footerHeight: 10,
    bottomBarHeight: 60,
  },
  // global style override of mui components
  components: {
    MuiLink: {
      styleOverrides: {
        root: {
          // color: "#7daee1",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorInherit: {
          //  backgroundColor: '#303030',
          // color: '#000',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          "*::-webkit-scrollbar": {
            width: "0.5em",
            height: "0.5em",
          },
          "*::-webkit-scrollbar-track": {
            WebkitBoxShadow: "inset 0 0 6px rgba(205,205,205,0.00)",
          },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.4)",
            borderRadius: "10px",
          },
        },
      },
    },
  },
});

function MyThemeProvider(props: { children: React.ReactNode }) {
  const type = useAppSelector((state: ApplicationState) => state.applicationLayout.type);

  function getTheme() {
    if (type === "dark") return darkTheme;
    else return lightTheme;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      {props.children}
    </ThemeProvider>
  );
}

export { MyThemeProvider };
